import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const AuthContext = createContext();

const leerUsuarioGuardado = () => {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) return null;
  try {
    return JSON.parse(storedUser);
  } catch (e) {
    return { user: storedUser };
  }
};

const haySesion = () => !!(localStorage.getItem("access_token") || localStorage.getItem("refresh_token"));

export function AuthProvider({ children }) {
  // `user` guardado en localStorage es texto editable desde la consola: solo vale
  // para pintar nombre/apellidos al arrancar. El ROL (tipo) con el que se decide
  // qué pantallas se enseñan se pide al servidor (/login/me devuelve el tipo del
  // JWT firmado) antes de dejar entrar a ninguna ruta protegida.
  // Sin tokens no hay sesión, aunque haya un `user` guardado (o inventado).
  const [user, setUser] = useState(() => (haySesion() ? leerUsuarioGuardado() : null));
  const [verificado, setVerificado] = useState(() => !haySesion());

  useEffect(() => {
    if (verificado) return undefined;
    let cancelado = false;
    const comprobar = async (intento = 0) => {
      try {
        // _connRetried: sin el aviso global de reconexión; si no hay red se va a /login,
        // que muestra "Sin conexión" y entra solo cuando vuelve (la sesión no se borra)
        const { data } = await axios.get("/api/login/me", { timeout: 12000, _connRetried: true });
        if (cancelado) return;
        const confirmado = { ...(leerUsuarioGuardado() || {}), ...data };
        localStorage.setItem("user", JSON.stringify(confirmado));
        setUser(confirmado);
        setVerificado(true);
      } catch (e) {
        if (cancelado) return;
        const status = e?.response?.status;
        if (status === 401 || status === 403 || status === 404) {
          // Token inválido / caducado sin refresh / usuario de baja: fuera.
          setUser(null);
          setVerificado(true);
        } else if (intento < 3) {
          setTimeout(() => comprobar(intento + 1), 2000);
        } else {
          // Sin conexión con el servidor: no se puede confirmar el rol. No se
          // abre ninguna ruta protegida con el rol guardado (manipulable).
          setUser(null);
          setVerificado(true);
        }
      }
    };
    comprobar();
    return () => { cancelado = true; };
  }, [verificado]);

  // Acepta el objeto completo devuelto por /api/login/, que incluye
  // access_token y refresh_token. Los guarda por separado y mantiene
  // el resto de los datos del usuario en `user`.
  const login = (loginResponse) => {
    if (!loginResponse) return;
    const { access_token, refresh_token, ...userData } = loginResponse;
    if (access_token) localStorage.setItem("access_token", access_token);
    if (refresh_token) localStorage.setItem("refresh_token", refresh_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    setVerificado(true); // viene directamente del servidor (/login o /login/me)
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  };

  return (
    <AuthContext.Provider value={{ user, verificado, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
